import { cn } from "utils/helpers";

interface ToggleButtonGroupOption {
	label: string;
	value: string;
}

interface ToggleButtonGroupProps {
	options: ToggleButtonGroupOption[];
	value: string | null;
	onChange: (value: string) => void;
	/**
	 * When true every button is non-interactive: native `disabled`,
	 * `tabIndex={-1}` (removed from the tab order so the keyboard cannot
	 * activate it) and `aria-disabled`. Optional/back-compatible (default false).
	 */
	disabled?: boolean;
}

export const ToggleButtonGroup: React.FC<ToggleButtonGroupProps> = ({
	options,
	value,
	onChange,
	disabled = false,
}) => {
	return (
		<div className="inline-flex gap-2" role="group">
			{options.map((option) => {
				const isActive = option.value === value;

				return (
					<button
						key={option.value}
						type="button"
						aria-pressed={isActive}
						aria-disabled={disabled}
						disabled={disabled}
						tabIndex={disabled ? -1 : undefined}
						onClick={() => {
							if (!disabled) {
								onChange(option.value);
							}
						}}
						className={cn(
							"rounded-lg border px-4 py-2 text-sm font-medium transition-colors",
							"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-2",
							disabled && "cursor-not-allowed opacity-60",
							isActive
								? "border-[#0031DD] bg-[#0031DD] text-white"
								: "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
						)}
						data-testid={`toggle-btn-${option.value}`}
					>
						{option.label}
					</button>
				);
			})}
		</div>
	);
};
